/**
 * Query string helpers — the canonical `qs` config for the project.
 *
 * One wire shape, used everywhere: bracket notation for nested objects
 * (`filters[name][ilike]=foo`), comma form for arrays (`ids=a,b,c`).
 * Both HTTP requests and browser URL state stringify with the same config,
 * so a URL produced by one helper round-trips through any other.
 *
 * The two parsers diverge in one detail only: `parseQueryString` (HTTP)
 * coerces booleans and numerics so backend Zod schemas can use plain
 * `z.boolean()` / `z.number()`. `parseSearchString` (URL state) skips that
 * coercion because TanStack Router routes already use `z.string()` /
 * `z.coerce.X()` and shouldn't have values silently retyped under them.
 * Both share the same recursive array-flatten step and the same wire format.
 *
 * See `docs/query-params.md` for the full project standard.
 */

import * as qs from "qs";

/**
 * Parse an HTTP query string into a typed object.
 *
 * - Bracket notation: `filters[roles]=MANAGER` → `{ filters: { roles: "MANAGER" } }`
 * - Numeric coercion: `take=10` → `{ take: 10 }`
 * - Boolean coercion: `active=true` → `{ active: true }`
 * - Leading-zero strings preserved: `s=0123` → `{ s: "0123" }`
 * - String arrays flattened to comma form at every depth:
 *     `ids[]=a&ids[]=b` → `{ ids: "a,b" }`
 *     `filters[ids][in][]=a&filters[ids][in][]=b` → `{ filters: { ids: { in: "a,b" } } }`
 *   so list-shaped values look identical regardless of wire form. Pair with
 *   `commaListSchema` from `@birdwatch/common` (see `zod-utils.ts`) to split back
 *   into `string[]`.
 *
 * Single source of truth for backend request parsing. Do not call `qs.parse`
 * directly in route handlers — the framework wires this in for you.
 *
 * @param queryString - Query string without the leading `?`
 */
export function parseQueryString(queryString: string): Record<string, unknown> {
  const parsed = qs.parse(queryString, {
    decoder: (value, defaultDecoder, charset, type) => {
      if (type === "value") {
        if (value === "true") return true;
        if (value === "false") return false;
        if (/^\d+$/.test(value)) {
          return /^0\d+$/.test(value) ? value : Number(value);
        }
        if (/^\d+\.\d+$/.test(value)) return Number.parseFloat(value);
      }
      return defaultDecoder(value, charset);
    },
  }) as Record<string, unknown>;

  return flattenStringArraysDeep(parsed) as Record<string, unknown>;
}

/**
 * Recursively walk a parsed-qs object. Any array of strings — at any depth —
 * is joined to a comma string. Mixed/object arrays are walked element-wise.
 */
function flattenStringArraysDeep(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.every((v) => typeof v === "string")
      ? (value as string[]).join(",")
      : value.map(flattenStringArraysDeep);
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = flattenStringArraysDeep(v);
    }
    return out;
  }
  return value;
}

/**
 * Parse the query string out of a full URL.
 */
export function parseQueryFromUrl(url: string): Record<string, unknown> {
  return parseQueryString(new URL(url).search.slice(1));
}

/**
 * Serialize an object to an HTTP query string with the project-canonical
 * config: comma arrays, encoded values, null/undefined skipped.
 *
 * @param obj - Object to stringify
 * @param options.addPrefix - Prepend `?` to the result (default: `false`)
 */
export function stringifyQuery(
  obj: Record<string, unknown>,
  options?: { addPrefix?: boolean },
): string {
  const queryString = qs.stringify(obj, {
    encode: true,
    skipNulls: true,
    arrayFormat: "comma",
  });
  return options?.addPrefix && queryString ? `?${queryString}` : queryString;
}

/**
 * Parse browser URL state for TanStack Router. Strips `?` if present.
 * Same wire format as `parseQueryString` (recursive array flatten, comma form)
 * but skips boolean/numeric value coercion — route schemas use `z.string()`
 * and `z.coerce.X()` and rely on raw string values arriving at validation.
 */
export function parseSearchString(search: string): Record<string, unknown> {
  const stripped = search.startsWith("?") ? search.slice(1) : search;
  const parsed = qs.parse(stripped) as Record<string, unknown>;
  return flattenStringArraysDeep(parsed) as Record<string, unknown>;
}

/**
 * Serialize a TanStack Router search object back to a URL fragment with
 * the canonical config — comma arrays, encoded values, null/undefined skipped.
 * Matches `stringifyQuery` so wire shape is identical to HTTP requests.
 */
export function stringifySearchObject(
  searchObj: Record<string, unknown>,
): string {
  return qs.stringify(searchObj, {
    encode: true,
    skipNulls: true,
    arrayFormat: "comma",
    addQueryPrefix: true,
  });
}
